import type { MaybeRefOrGetter, Ref } from 'vue'
import { nextTick, ref, toValue, watch } from 'vue'
import useResizeObserver from '@app/shared/vue/composables/use-resize-observer'

/**
 * A Vue composable that uses the size of an element to check whether it has
 * been truncated, and either populates or clears a string ref based on that
 * info. The check re-runs whenever the element is resized and whenever the
 * text changes (pass a ref/getter for a reactive label).
 * @param containerRef - A ref to the element to observe
 * @param text - The content of the ref when the element has been truncated
 * @returns A ref containing either the given text or the empty string
 */
export default function useDynamicTooltip(
  containerRef: Ref<HTMLElement | null>,
  text: MaybeRefOrGetter<string | number | null | undefined>
): Ref<string> {
  const tooltip = ref<string>('')
  const { entries } = useResizeObserver(containerRef)

  const applyTruncation = (element: HTMLElement | null): void => {
    if (!element) {
      return
    }

    tooltip.value =
      element.offsetWidth < element.scrollWidth
        ? String(toValue(text) ?? '')
        : ''
  }

  watch(entries, () => {
    if (entries.value.length === 0) {
      return
    }

    applyTruncation(entries.value[0].target as HTMLElement)
  })

  watch(
    () => toValue(text),
    () => nextTick(() => applyTruncation(containerRef.value)),
    { immediate: true }
  )

  return tooltip
}
